"use client"

import {
  CONTAINER_ICON_HEIGHT,
  DOCK_MARGIN_BOTTOM_REM,
  DOCK_PADDING,
} from "./dock-config"
import { useCallback, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { DOCK_NAV_ITEMS } from "../nav-config"
import { DockItem } from "./DockItem"
import { useDockAnimation } from "./useDockAnimation"
import { useDockResponsive } from "./useDockResponsive"
import { useIntl } from "react-intl"

export function AppDock() {
  const intl = useIntl()
  const pathname = useLocation().pathname
  const navigate = useNavigate()
  const dockRef = useRef<HTMLDivElement>(null)

  const { config, baseSpacing } = useDockResponsive()
  const { maxScale, effectWidth } = config

  const {
    currentScales,
    currentPositions,
    contentWidth,
    handleMouseMove,
    handleMouseLeave,
    setMouseX,
    iconRefs,
  } = useDockAnimation({
    itemCount: DOCK_NAV_ITEMS.length,
    maxScale,
    effectWidth,
    baseSpacing,
    dockRef,
  })

  const handleClick = useCallback(
    (path: string, index: number) => {
      setMouseX(null)
      const el = iconRefs.current[index]
      if (el) {
        const scale = currentScales[index] ?? 1
        const bounceY =
          scale > 1.3
            ? -CONTAINER_ICON_HEIGHT * 0.2
            : -CONTAINER_ICON_HEIGHT * 0.14
        el.style.transition = "transform 0.2s ease-out"
        el.style.transform = `translateY(${bounceY}px)`
        setTimeout(() => {
          el.style.transform = "translateY(0)"
        }, 200)
      }
      navigate(path)
    },
    [navigate, currentScales, setMouseX, iconRefs]
  )

  const barHeight = CONTAINER_ICON_HEIGHT + DOCK_PADDING * 2

  return (
    <nav
      aria-label={intl.formatMessage({ id: "layout.navLabel" })}
      className="pointer-events-none fixed right-0 bottom-0 left-0 z-50 flex justify-center px-3"
      style={{
        paddingBottom: `max(${DOCK_MARGIN_BOTTOM_REM}rem, env(safe-area-inset-bottom))`,
      }}
    >
      <div
        ref={dockRef}
        role="group"
        className="border-border bg-background shadow-1-light pointer-events-auto rounded-full border"
        style={{
          width: `${contentWidth + DOCK_PADDING * 2}px`,
          height: `${barHeight}px`,
          padding: `${DOCK_PADDING}px`,
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="relative w-full"
          style={{ height: `${CONTAINER_ICON_HEIGHT}px` }}
        >
          {DOCK_NAV_ITEMS.map((item, index) => {
            const isActive =
              pathname === item.path ||
              (item.path !== "/" && pathname.startsWith(item.path + "/"))
            return (
              <DockItem
                key={item.path}
                item={item}
                index={index}
                scale={currentScales[index] ?? 1}
                position={currentPositions[index] ?? 0}
                isActive={isActive}
                onClick={handleClick}
                iconRef={(el) => {
                  iconRefs.current[index] = el
                }}
              />
            )
          })}
        </div>
      </div>
    </nav>
  )
}
